import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { InnerMessage, InnerMessagesService } from '../../../../shared/service/inner-messages.service';
import { Utilities as U } from '../../../../shared/utilities/utilities.model';
import { ChangePasswordService, ResponseValidatePassword } from '../../../services/change-password.service';
import { TranslateContext, TranslatePipe } from '@shared/pipes/translate.pipe';
import { AppConfigEnvironmentService } from '@shared/service/appconfig-environment.service';
import { LoginRequestService } from '@shared/service/login-request.service';
import { ErrorResponse } from '@shared/models/error-response.model';
import { HTTPStatusCode } from '@shared/models/http-status-code.model';
import { MessageManagerService } from '@shared/service/message-manager/message-manager.service';
import { MessageComponentType, MessageType } from '@shared/service/message-manager/message.model';

export interface ProfileResetPasswordDialogComponentInputData {
	username: string;
}
type ErrorMessageResponse = { displayValue: string, placeHolderValueStrings: string[], resourceBundleKey: string, };

@Component({
	selector: 'amd-profile-reset-password',
	templateUrl: './profile-reset-password-dialog.component.html',
	styleUrls: ['./profile-reset-password-dialog.component.scss']
})
export class ProfileResetPasswordDialogComponent implements OnInit {
	formResetPassword: FormGroup;
	regx: string;
	canSavePassword: boolean = false;
	translateContext: TranslateContext = TranslateContext.PROFILE;
	formField: typeof EFormField = EFormField;
	formError: typeof EFormError = EFormError;
	username: string = '';
	newPasswordCache: string = '';
	messagePasswordChanged: string = '';

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: ProfileResetPasswordDialogComponentInputData,
		public fb: FormBuilder,
		public dialogRef: MatDialogRef<ProfileResetPasswordDialogComponent>,
		public changePasswordService: ChangePasswordService,
		private appConfig: AppConfigEnvironmentService,
		private translatePipe: TranslatePipe,
		public innerMessagesService: InnerMessagesService,
		private messageManagerService: MessageManagerService,
	) {
		this.formResetPassword = this.fb.group(
			{
				[EFormField.current_password]     : ['', Validators.required],
				// [EFormField.new_password]         : ['', [Validators.required, Validators.pattern(this.regx)]],
				[EFormField.new_password]         : ['', [Validators.required]],
				[EFormField.new_password_confirm] : ['', Validators.required],
			}, 
			{ validator: this.MatchPasswordValidator }
		);
	}

	ngOnInit(): void {
		this.username = this.data.username;
	}

  MatchPasswordValidator(c: AbstractControl): ValidationErrors | null {
		
		const password = {
			'password'         : c.get(EFormField.new_password).value,
			'password_confirm' : c.get(EFormField.new_password_confirm).value,
		};

		if (U.isEmpty(password['password']) || U.isEmpty(password['password_confirm'])) {
			return null;
		}

		return (password['password'] !== password['password_confirm']) ? {[EFormError.match_password]: true} : null;
  }

	keyHandler(e: any, key: string): void {

		const CAPS_LOCK_KEY = 'CapsLock';
		const form = this.formResetPassword.controls[key];

		if (!e.target.value) {
			form.setErrors({[EFormError.required]: true});	
			return;
		}

		if (e instanceof KeyboardEvent) {

			if (e.code === 'Tab') {
				return;
			}

			if (e.getModifierState(CAPS_LOCK_KEY)) {
				form.setErrors({[this.formError.cap_lock_active]: true});	
			}
		}
	}
		
	onBlurInput(event: any): void {
		const form = this.formResetPassword.controls;
		const password = {
			'current' : form[EFormField.current_password].value,
			'new'     : form[EFormField.new_password].value,
			'confirm' : form[EFormField.new_password_confirm].value,
		};
		this.canSavePassword = Object.keys(password).map(x => !U.isEmpty(password[x])).reduce((x,y) => x || y);
	}
		
	onBlurNewPassword(event: any): Promise<void> {
		
		const form = this.formResetPassword.controls;
		const password = {
			'current' : form[EFormField.current_password].value,
			'new'     : form[EFormField.new_password].value,
			'confirm' : form[EFormField.new_password_confirm].value,
		};
		
		if (U.isEmpty(password['current']) || U.isEmpty(password['new'])) {
			// TODO
			// inserire step per disabilitare la password nuova finche non va bene quella vecchia
			return;
		}

		if (this.newPasswordCache === password['new']) {
			return;
		}
		
		// Errore per conferma password se non coincidono
		this.isNewPasswordTheSame(password['new'], password['confirm'])

		try {
			this.canSavePassword = false;

			// La verifica della password viene fatta lato OAM
			this.changePasswordService.checkNewPasswordOAM(this.username, password['current'], password['new']).subscribe(
				res => {
					// this.canSavePassword = true;
					this.changePasswordService.setStatusChangeOAM(this.username, password['current']).subscribe(
						res => {
							this.canSavePassword = true;
						}, e => {
							console.error(e);
						}
					);
				}, e => {
					console.error(e);

					// Stampa messaggi errori policy nuova password
					if (e instanceof ResponseValidatePassword) {
						e.messages.forEach(x => this.showErrorMessageFromGeut(x));
						
						// e.messages.forEach(x => {
						// 	// nell'enum non è stato possibile utilizzare il carattere -
						// 	const id = x.resourceBundleKey.replace('-', '_');
						// 	this.formResetPassword.controls[EFormField.new_password_confirm].setErrors({[EFormError[id]]: true});
						// });
					}

					// La password corrente è sbagliata
					if (e instanceof ErrorResponse) {
						if (e.status === HTTPStatusCode[401]) {
							this.formResetPassword.controls[EFormField.current_password].setErrors({[EFormError.wrong_current_password]: true});
						}
					}

					// TODO
					// ??? non dovrebbe essere false?
					this.canSavePassword = true;
				}, () => {
					// Stranamente non entra nel giro
					this.canSavePassword = true;
				}
			);

		} catch (e) {
			
				console.error(e);
				const inMes: InnerMessage = new InnerMessage(InnerMessage.CHANGED_PASSWORD_KO, 'error', null, true);
				this.innerMessagesService.pushInnerMessage(inMes);
		}

		this.newPasswordCache = password['new'];
	}	
	
	onBlurConfirmPassword(event: any): void {
		const form = this.formResetPassword.controls;
		const password = {
			'current' : form[EFormField.current_password].value,
			'new'     : form[EFormField.new_password].value,
			'confirm' : form[EFormField.new_password_confirm].value,
		};

		// tutti i campi devono essere riempiti
		if (!Object.keys(password).map(x => !U.isEmpty(password[x])).reduce((x,y) => x && y)) {
			return;
		}

		// Errore se la nuova password e quella di conferma non coincidono
		this.isNewPasswordTheSame(password['new'], password['confirm']);
	}

	isNewPasswordTheSame(passwordNew: string, passwordConfirm: string): boolean {

		if (U.isEmpty(passwordConfirm)) {
			return false;
		}

		const res = passwordNew === passwordConfirm;
		
		this.formResetPassword.controls[EFormField.new_password_confirm].setErrors( (!res ? {[EFormError.match_password]: true} : null) );
		return res;
	}
	
	save($event: Event): void {
		
		if (this.formResetPassword.valid) {
			
			const form = this.formResetPassword.controls;
			const password = {
				'current' : form[EFormField.current_password].value,
				'new'     : form[EFormField.new_password].value,
			};

			if (!U.isEmpty(password['new'])) {

				this.canSavePassword = false;
				this.changePasswordService.setPasswordOAM(this.username, password['current'], password['new']).subscribe(
					res => {

						if (res instanceof ResponseValidatePassword && res.isValid) {
							// Se il cambio password è ok, l'utente è costretto a rifare il login
							LoginRequestService.logout();
							return;	
						}

						// Errore
						// Viene stampato il messaggio e l'utente può tentare con un'altra password

						if (
							res instanceof ErrorResponse &&
							res.message === ResponseChangePasswordServiceEnum.ERROR_OCCURRED_DURING_PASSWORD_POLICY_CHECK
						) {
							form[EFormField.current_password].setErrors({pattern: true});
						}
						this.innerMessagesService.pushInnerMessage(new InnerMessage(InnerMessage.CHANGED_PASSWORD_KO, MessageType.TYPE_ERROR, null, true) );
						this.canSavePassword = true;

					}, e => {							
						if (e instanceof ResponseValidatePassword) {
							
							// non dovrebbe avere i messaggi di errore di GEUT
							e.messages.forEach(x => this.showErrorMessageFromGeut(x));
							this.canSavePassword = true;
						}
					}, () => {
						// Stranamente non entra nel giro
						this.canSavePassword = true;
					},
				);
			}
		}
	}
	
	showErrorMessageFromGeut(x: ErrorMessageResponse): void {
		
			// Messaggio nell'header della dialog
			if (x.resourceBundleKey === EFormError.IAM_3040034) {

				this.messageManagerService.pushInnerMessage({
					code          : null,
					message       : 'La password non può essere cambiata fino a ' + x.placeHolderValueStrings,
					type          : MessageType.TYPE_ERROR,
					componentType : MessageComponentType.FLASH_MESSAGE,
					isTop         : false
				});
				return;
			}

			// nell'enum non è stato possibile utilizzare il carattere -
			const id = x.resourceBundleKey.replace('-', '_'); 
			this.formResetPassword.controls[EFormField.new_password].setErrors({[EFormError[id]]: true});
	}
}

enum ResponseChangePasswordServiceEnum {
	'ERROR_OCCURRED_DURING_PASSWORD_POLICY_CHECK'= 'ERROR OCCURRED DURING PASSWORD POLICY CHECK'
}

enum EFormField {
	current_password 		 = 'current_password',
	new_password 			   = 'new_password', 
	new_password_confirm = 'new_password_confirm',
}

enum EFormError {
	required		  		  			   				      = 'required', 
	match_password		  			   				      = 'match_password', 
	match_server_passwords 			   			    	= 'match_server_passwords', 
	match_server_old_passwords 			   				= 'match_server_old_passwords', 
	match_current_passwords_same_new_password = 'match_current_passwords_same_new_password', 
	cap_lock_active 												  = 'cap_lock_active', 
	wrong_current_password 									  = 'wrong_current_password', 
	
	// Errori da BE
	IAM_3053056	    												  = 'IAM-3053056', 
	IAM_3053049	    												  = 'IAM-3053049', 
	IAM_3053050	    												  = 'IAM-3053050', 
	IAM_3053052	    												  = 'IAM-3053052', 
	IAM_3053063	    												  = 'IAM-3053063', 
	IAM_3040034	    												  = 'IAM-3040034', 
	IAM_3053054	    												  = 'IAM-3053054', 
}

const MIN_NUMBER_CHAR_PASSWORD = 10;
